import { useEffect, useState } from "react";
import api, { apiError, fmtMXN, fmtDate } from "@/lib/api";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";

const empty = { supplier_id: "", folio: "", order_id: "", subtotal: 0, iva: 0, total: 0, fecha: "", fecha_vencimiento: "", estatus_liquidacion: "pendiente" };

const ISTATUS = [["pendiente", "Pendiente"], ["liquidada", "Liquidada"]];
const COLUMNS = [
  { key: "folio", label: "Folio", accessor: (f) => f.folio, placeholder: "Folio" },
  { key: "proveedor", label: "Proveedor", accessor: (f) => f.supplier_snapshot?.razon_social, placeholder: "Proveedor" },
  { key: "pedido", label: "Pedido", accessor: (f) => f.order_folio, placeholder: "Pedido" },
  { key: "total", label: "Total", accessor: (f) => f.total, placeholder: "Total" },
  { key: "vence", label: "Vence", accessor: (f) => fmtDate(f.fecha_vencimiento), sortAccessor: (f) => f.fecha_vencimiento, placeholder: "Vence" },
  { key: "estatus", label: "Estatus", type: "select", accessor: (f) => f.estatus_liquidacion, options: ISTATUS.map(([value, label]) => ({ value, label })) },
  { key: "_a", type: "none" },
];

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const tools = useTableTools(rows, COLUMNS);

  const load = () => api.get("/invoices").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSuppliers(r.data)); api.get("/orders").then((r) => setOrders(r.data)); }, []);

  const setTotals = (sub) => { const s = Number(sub); setForm((f) => ({ ...f, subtotal: s, iva: +(s * 0.16).toFixed(2), total: +(s * 1.16).toFixed(2) })); };

  const save = async () => {
    try { await api.post("/invoices", { ...form, order_id: form.order_id || null }); toast.success("Factura registrada"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const liquidate = async (iid) => { await api.put(`/invoices/${iid}/liquidate`); toast.success("Factura liquidada"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Cuentas por Pagar</h1><p className="text-muted-foreground text-sm">Facturas de proveedores. Liquidadas alimentan Gastos del Dashboard.</p></div>
        <div className="flex items-center gap-2">
          <TableToolbar tools={tools} columns={COLUMNS} filename="cuentas-x-pagar" />
          <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-2" /> Registrar factura</Button>
        </div>
      </div>
      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
            {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
          </TableHeader>
          <TableBody>
            {tools.view.map((f) => (
              <TableRow key={f.id} data-testid="invoice-row">
                <TableCell className="font-mono">{f.folio}</TableCell>
                <TableCell>{f.supplier_snapshot?.razon_social}</TableCell>
                <TableCell className="font-mono text-xs">{f.order_folio || "—"}</TableCell>
                <TableCell>{fmtMXN(f.total)}</TableCell>
                <TableCell>{fmtDate(f.fecha_vencimiento)}</TableCell>
                <TableCell><Badge variant={f.estatus_liquidacion === "liquidada" ? "default" : "secondary"}>{f.estatus_liquidacion}</Badge></TableCell>
                <TableCell>{f.estatus_liquidacion !== "liquidada" && <Button size="sm" variant="outline" onClick={() => liquidate(f.id)} data-testid="liquidate-btn"><Check className="w-4 h-4 mr-1" /> Liquidar</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin facturas aún.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar factura de compra</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Proveedor *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="invoice-supplier"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.razon_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Folio factura *</Label><Input value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} data-testid="invoice-folio" /></div>
            <div><Label>Pedido asociado</Label>
              <Select value={form.order_id} onValueChange={(v) => setForm({ ...form, order_id: v })}>
                <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.folio}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subtotal</Label><Input type="number" value={form.subtotal} onChange={(e) => setTotals(e.target.value)} data-testid="invoice-subtotal" /></div>
            <div><Label>IVA</Label><Input type="number" value={form.iva} readOnly /></div>
            <div><Label>Total</Label><Input type="number" value={form.total} readOnly /></div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>Vencimiento</Label><Input type="date" value={form.fecha_vencimiento} onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-invoice">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
